import { Select, SelectProps } from '@cloudscape-design/components';
import { convertItem, convertList } from 'src/utils/generalUtils';
import { useMemo, useCallback, memo } from 'react';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';

interface BusinessUnitListDropdownProps {
  businessUnitList: BusinessUnit[];
  isLoading: boolean;
  error?: string | null;
  selectedBusinessUnit?: BusinessUnit;
  onBusinessUnitChange: (bu: BusinessUnit) => void;
  readOnly?: boolean;
}

const BusinessUnitListDropdown = ({
  selectedBusinessUnit,
  businessUnitList,
  isLoading,
  error,
  readOnly,
  onBusinessUnitChange,
}: BusinessUnitListDropdownProps) => {
  // Memoize the selected role conversion
  const selectedBusinessUnitOption = useMemo(() => {
    if (!selectedBusinessUnit) return null;
    return convertItem<BusinessUnit, SelectProps.Option>(
      selectedBusinessUnit,
      (item) => ({
        value: item.businessUnitId,
        label: item.name,
      }),
    );
  }, [selectedBusinessUnit]);

  // Memoize the roles list conversion
  const businessUnitOptions = useMemo(() => {
    return convertList<BusinessUnit, SelectProps.Option>(businessUnitList, (item) => ({
      value: item.businessUnitId,
      label: item.name,
    }));
  }, [businessUnitList]);

  // Memoize the status type calculation
  const statusType = useMemo(() => {
    if (isLoading) return 'loading';
    if (error) return 'error';
    return 'finished';
  }, [isLoading, error]);

  // Memoize the onChange handler
  const handleChange = useCallback(
    ({ detail }: { detail: SelectProps.ChangeDetail }) => {
      if (detail.selectedOption?.value) {
        const selectedBU = businessUnitList.find(
          (item) => item.businessUnitId === detail.selectedOption.value,
        );
        if (selectedBU) {
          onBusinessUnitChange(selectedBU);
        }
      }
    },
    [businessUnitList, onBusinessUnitChange],
  );

  return (
    <Select
      readOnly={readOnly}
      data-testid="business-unit-dropdown"
      options={businessUnitOptions}
      selectedOption={selectedBusinessUnitOption}
      onChange={handleChange}
      errorText={error || undefined}
      loadingText="Loading Business Units"
      statusType={statusType}
      placeholder="Choose a Business Unit"
      filteringType="auto"
    />
  );
};

// Memoize the entire component if needed
export default memo(BusinessUnitListDropdown, (prevProps, nextProps) => {
  // Custom comparison function
  return (
    prevProps.businessUnitList === nextProps.businessUnitList &&
    prevProps.selectedBusinessUnit?.businessUnitId ===
      nextProps.selectedBusinessUnit?.businessUnitId &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.error === nextProps.error
  );
});
